import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import PlusIcon from "@src/lib/icons/18px/plus";
import SuitcaseOutlineIcon from "@src/lib/icons/18px/suitcase-outline";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useIsFullAdmin from "@src/features/app/hooks/useIsFullAdmin";

import { useIsComplianceOnly } from "@src/features/app/hooks/useIsComplianceOnly";
import Text from "@src/lib/ui/Text";
import MessageDialogGate, {
  useCanCreateChats,
} from "@src/features/messaging/MessageDialogGate";
import MessageActionIcon from "@src/features/messaging/MessageActionIcon";
import InviteAdminOrManagerDialog from "@src/features/users/InviteAdminOrManagerDialog";
import InviteTraineeDialog from "@src/features/users/InviteTraineeDialog";
import BookOpenIcon from "@src/lib/icons/18px/book-open-outline";
import BookmarkIcon from "@src/lib/icons/18px/bookmark-outline";

import UsersOutlineIcon from "@src/lib/icons/18px/users-outline";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import CircleCheckmarkOutlineIcon from "@src/lib/icons/18px/circle-checkmark-outline";
import ArrowUploadIcon from "@src/lib/icons/18px/arrow-upload";
import FilePlusOutlineIcon from "@src/lib/icons/18px/file-plus-outline";
import BulkUploadTrainingResourceDialog from "@src/features/app/dialogs/bulkUploadTrainingResourceDialog/BulkUploadTrainingResourceDialog";
import NoPermissionsTooltip from "@src/lib/ui/NoPermissionsTooltip";
import { Ff, LibraryItemType } from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import ContentTypeIcon from "@src/features/contentLibrary/contentTypeIcons";
import ConvertPdfToOpusDocDialog from "@src/features/shared/ConvertPdfToOpusDocDialog";
import { useCreateBlankRichTextDocumentMutation } from "@src/features/library/resources/ResourcesPageHeader.generated";
import UploadSCORMDialog from "@src/features/scorm/dialogs/UploadSCORMDialog";
import CourseCreationFlowDialog from "@src/features/contentLibrary/CourseCreationFlowDialog";
import ModuleCreationFlowDialog from "@src/features/contentLibrary/ModuleCreationFlowDialog";
import OutlineCreationFlowDialog from "@src/features/contentLibrary/OutlineCreationFlowDialog";
import { OutlineFlowType } from "@src/features/contentLibrary/outlineFlows.config";
import { useToast } from "@src/lib/hooks/useToast";
import { useCheckInsPageHeaderCreateSkillMutation } from "@src/features/library/check-ins/CheckInsPageHeader.generated";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import router, { useRouter } from "next/router";

type NavMenuAddButtonProps = {
  /**
   * Optional custom trigger element. When provided it replaces the default
   * compact "+" square button (e.g. to render the action as a nav rail row).
   */
  trigger?: React.ReactNode;
  /** When true, only show library/training create actions (no Message / Invite). */
  trainingContentOnly?: boolean;
  /** Menu placement relative to the trigger. Sidebar + defaults to opening below. */
  menuSide?: "top" | "bottom" | "left" | "right";
  menuAlign?: "start" | "center" | "end";
  onMenuOpenChange?: (open: boolean) => void;
};

const NavMenuAddButton: React.FC<NavMenuAddButtonProps> = ({
  trigger,
  trainingContentOnly = false,
  menuSide = "bottom",
  menuAlign = "end",
  onMenuOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [scormDialogOpen, setScormDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const setMenuOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onMenuOpenChange?.(nextOpen);
  };

  const { userActionPermissions } = useActionPermissions();
  const canCreateChats = useCanCreateChats();
  const { addErrorToast } = useToast();
  const routerInstance = useRouter();

  const isComplianceOnly = useIsComplianceOnly();
  const isFullAdmin = useIsFullAdmin();
  const disableNavMenuAddButton = trainingContentOnly
    ? !userActionPermissions?.createContent
    : !userActionPermissions?.createContent &&
      !userActionPermissions?.inviteOthers &&
      !canCreateChats;
  const [isModuleCreationFlowOpen, setIsModuleCreationFlowOpen] =
    useState(false);
  const [isCourseCreationFlowOpen, setIsCourseCreationFlowOpen] =
    useState(false);
  const [isTaskListCreationFlowOpen, setIsTaskListCreationFlowOpen] =
    useState(false);
  const [isSkillCreationFlowOpen, setIsSkillCreationFlowOpen] = useState(false);
  const [isAuditCreationFlowOpen, setIsAuditCreationFlowOpen] = useState(false);
  const [isInviteTraineeOpen, setIsInviteTraineeOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isInviteAdminOpen, setIsInviteAdminOpen] = useState(false);
  const chatTaskListOutlinesEnabled = useFeatureFlag(Ff.ChatTaskListOutlines);
  const chatCheckInOutlinesEnabled = useFeatureFlag(Ff.ChatCheckInOutlines);
  const chatAuditOutlinesEnabled = useFeatureFlag(Ff.ChatAuditOutlines);
  const richTextDocumentEnabled = useFeatureFlag(Ff.RichTextDocument);

  // Direct creation mutations (fallback when feature flags are off)
  // Separate handlers to preserve original callsites
  const [createSkillMutation] = useCheckInsPageHeaderCreateSkillMutation();
  const [createTaskMutation] = useCheckInsPageHeaderCreateSkillMutation();
  const [createAuditMutation] = useCheckInsPageHeaderCreateSkillMutation();

  const [createBlankRichTextDocument, { loading: creatingBlankDoc }] =
    useCreateBlankRichTextDocumentMutation({
      onCompleted(data) {
        const documentLibraryItemId =
          data.createTrainingResourceV2?.trainingResource?.libraryItem.id;
        if (!documentLibraryItemId) return;
        routerInstance.push({
          pathname: "/library/library-item/[id]",
          query: { id: documentLibraryItemId },
        });
      },
    });

  const handleCreateSkill = async () => {
    const res = await createSkillMutation({
      variables: {
        input: {
          type: LibraryItemType.Skill,
          name: emptyTranslationSet(),
          description: emptyTranslationSet(),
        },
      },
    });
    const libraryItemId = res?.data?.createSkill.skill?.libraryItem.id;
    if (libraryItemId) {
      router.push({
        pathname: "/library/library-item/[id]",
        query: { id: libraryItemId },
      });
    } else {
      addErrorToast({
        ...res?.data?.createSkill,
        callsite: "create_skill_dialog",
      });
    }
  };

  const handleCreateTask = async () => {
    const res = await createTaskMutation({
      variables: {
        input: {
          type: LibraryItemType.Task,
          name: emptyTranslationSet(),
          description: emptyTranslationSet(),
        },
      },
    });
    const libraryItemId = res?.data?.createSkill.skill?.libraryItem.id;
    if (libraryItemId) {
      routerInstance.push({
        pathname: "/library/library-item/[id]",
        query: { id: libraryItemId },
      });
    } else {
      addErrorToast({
        ...res?.data?.createSkill,
        callsite: "create_task_dialog",
      });
    }
  };

  const handleCreateAudit = async () => {
    const res = await createAuditMutation({
      variables: {
        input: {
          type: LibraryItemType.Audit,
          name: emptyTranslationSet(),
          description: emptyTranslationSet(),
        },
      },
    });
    const libraryItemId = res?.data?.createSkill.skill?.libraryItem.id;
    if (libraryItemId) {
      router.push({
        pathname: "/library/library-item/[id]",
        query: { id: libraryItemId },
      });
    } else {
      addErrorToast({
        ...res?.data?.createSkill,
        callsite: "create_audit_dialog",
      });
    }
  };

  return (
    <>
      <ModuleCreationFlowDialog
        open={isModuleCreationFlowOpen}
        onOpenChange={setIsModuleCreationFlowOpen}
        onModuleCreated={(libraryItemId) => {
          if (libraryItemId) {
            routerInstance.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
        }}
      />
      <CourseCreationFlowDialog
        open={isCourseCreationFlowOpen}
        onOpenChange={setIsCourseCreationFlowOpen}
        onCourseCreated={(p) => {
          const libraryItemId = p.libraryItem.id;
          if (libraryItemId) {
            routerInstance.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
        }}
      />
      {chatTaskListOutlinesEnabled && (
        <OutlineCreationFlowDialog
          open={isTaskListCreationFlowOpen}
          onOpenChange={setIsTaskListCreationFlowOpen}
          flowType={OutlineFlowType.TaskList}
        />
      )}
      {chatCheckInOutlinesEnabled && (
        <OutlineCreationFlowDialog
          open={isSkillCreationFlowOpen}
          onOpenChange={setIsSkillCreationFlowOpen}
          flowType={OutlineFlowType.CheckIn}
        />
      )}
      {chatAuditOutlinesEnabled && (
        <OutlineCreationFlowDialog
          open={isAuditCreationFlowOpen}
          onOpenChange={setIsAuditCreationFlowOpen}
          flowType={OutlineFlowType.Audit}
        />
      )}
      {isInviteAdminOpen && (
        <InviteAdminOrManagerDialog
          open={isInviteAdminOpen}
          onOpenChange={setIsInviteAdminOpen}
        />
      )}
      {isInviteTraineeOpen && (
        <InviteTraineeDialog
          open={isInviteTraineeOpen}
          onOpenChange={setIsInviteTraineeOpen}
        />
      )}
      <DropdownMenu open={open} onOpenChange={setMenuOpen}>
        <NoPermissionsTooltip
          hasPermission={!disableNavMenuAddButton}
          tooltipSide="right"
        >
          <DropdownMenuTrigger asChild>
            {trigger ?? (
              <button className="flex size-7 items-center justify-center rounded-lg bg-gray-2 text-gray-11 hover:!bg-black-alpha-2">
                <PlusIcon className="!size-3.5" />
              </button>
            )}
          </DropdownMenuTrigger>
        </NoPermissionsTooltip>
        {!disableNavMenuAddButton && (
          <DropdownMenuContent
            side={menuSide}
            align={menuAlign}
            sideOffset={6}
            className="w-56"
          >
            {!isComplianceOnly && (
              <>
                <CreateContentRows
                  setOpen={setMenuOpen}
                  setPdfFile={setPdfFile}
                  setScormDialogOpen={setScormDialogOpen}
                  setBulkUploadOpen={setBulkUploadOpen}
                  onClickModule={() => {
                    if (!userActionPermissions?.createContent) return;
                    setIsModuleCreationFlowOpen(true);
                  }}
                  onClickCourse={() => {
                    if (!userActionPermissions?.createContent) return;
                    setIsCourseCreationFlowOpen(true);
                  }}
                  onClickTaskList={() => {
                    if (!userActionPermissions?.createContent) return;
                    if (chatTaskListOutlinesEnabled) {
                      setIsTaskListCreationFlowOpen(true);
                    } else {
                      handleCreateTask();
                    }
                  }}
                  onClickSkill={() => {
                    if (!userActionPermissions?.createContent) return;
                    if (chatCheckInOutlinesEnabled) {
                      setIsSkillCreationFlowOpen(true);
                    } else {
                      handleCreateSkill();
                    }
                  }}
                  onClickAudit={() => {
                    if (!userActionPermissions?.createContent) return;
                    if (chatAuditOutlinesEnabled) {
                      setIsAuditCreationFlowOpen(true);
                    } else {
                      handleCreateAudit();
                    }
                  }}
                  onClickOpusDoc={() => {
                    if (
                      !userActionPermissions?.createContent ||
                      creatingBlankDoc
                    ) {
                      return;
                    }
                    setMenuOpen(false);
                    createBlankRichTextDocument();
                  }}
                  richTextDocumentEnabled={richTextDocumentEnabled}
                  creatingBlankDoc={creatingBlankDoc}
                />
                {!trainingContentOnly && (
                  <>
                    <NoPermissionsTooltip hasPermission={canCreateChats}>
                      <DropdownMenuItem
                        onSelect={() => {
                          if (!canCreateChats) return;
                          setMenuOpen(false);
                          requestAnimationFrame(() => {
                            setIsMessageDialogOpen(true);
                          });
                        }}
                      >
                        <div className="flex items-center">
                          <MessageActionIcon className="mr-2 text-muted-foreground" />
                          <Text variant="compact-description">Message</Text>
                        </div>
                      </DropdownMenuItem>
                    </NoPermissionsTooltip>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        disabled={!userActionPermissions?.inviteOthers}
                      >
                        <NoPermissionsTooltip
                          hasPermission={userActionPermissions?.inviteOthers}
                        >
                          <div className="flex w-full items-center justify-between">
                            <div className="flex items-center">
                              <UsersOutlineIcon className="mr-2 text-muted-foreground" />
                              <Text variant="compact-description">
                                Invite Users
                              </Text>
                            </div>
                          </div>
                        </NoPermissionsTooltip>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <NoPermissionsTooltip
                            hasPermission={userActionPermissions?.inviteOthers}
                          >
                            <DropdownMenuItem
                              onSelect={() => {
                                if (!userActionPermissions?.inviteOthers)
                                  return;
                                setMenuOpen(false);
                                requestAnimationFrame(() =>
                                  setIsInviteTraineeOpen(true),
                                );
                              }}
                            >
                              <Text variant="compact-description">Trainee</Text>
                            </DropdownMenuItem>
                          </NoPermissionsTooltip>
                          <NoPermissionsTooltip
                            hasPermission={userActionPermissions?.inviteOthers}
                          >
                            <DropdownMenuItem
                              onSelect={() => {
                                if (!userActionPermissions?.inviteOthers)
                                  return;
                                setMenuOpen(false);
                                requestAnimationFrame(() =>
                                  setIsInviteAdminOpen(true),
                                );
                              }}
                            >
                              <Text variant="compact-description">
                                {isFullAdmin ? "Admin or Manager" : "Manager"}
                              </Text>
                            </DropdownMenuItem>
                          </NoPermissionsTooltip>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      {pdfFile && (
        <ConvertPdfToOpusDocDialog
          pdfFile={pdfFile}
          onClose={() => setPdfFile(null)}
        />
      )}
      <UploadSCORMDialog
        open={scormDialogOpen}
        onOpenChange={setScormDialogOpen}
      />
      {bulkUploadOpen && (
        <BulkUploadTrainingResourceDialog
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          onPdfFileUploaded={setPdfFile}
        />
      )}
      {isMessageDialogOpen && (
        <MessageDialogGate
          open={isMessageDialogOpen}
          onOpenChange={setIsMessageDialogOpen}
        />
      )}
    </>
  );
};

type CreateContentRowsProps = {
  setOpen: (open: boolean) => void;
  setPdfFile: (pdfFile: File | null) => void;
  setScormDialogOpen: (open: boolean) => void;
  setBulkUploadOpen: (open: boolean) => void;
  onClickModule: () => void;
  onClickCourse: () => void;
  onClickTaskList: () => void;
  onClickSkill: () => void;
  onClickAudit: () => void;
  onClickOpusDoc: () => void;
  richTextDocumentEnabled: boolean;
  creatingBlankDoc: boolean;
};

const CreateContentRows: React.FC<CreateContentRowsProps> = (props) => {
  const scormEnabled = useFeatureFlag(Ff.ScormResources);
  const { userActionPermissions } = useActionPermissions();
  const taskLibraryItemTypeFlagEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const auditsEnabled = useFeatureFlag(Ff.Audits);

  return (
    <>
      <NoPermissionsTooltip
        hasPermission={userActionPermissions?.createContent}
      >
        <DropdownMenuItem
          onSelect={() => {
            if (!userActionPermissions?.createContent) return;
            props.setOpen(false);
            props.onClickModule();
          }}
        >
          <div className="flex items-center">
            <BookOpenIcon className="mr-2 text-muted-foreground" />
            <Text variant="compact-description">Module</Text>
          </div>
        </DropdownMenuItem>
      </NoPermissionsTooltip>
      <NoPermissionsTooltip
        hasPermission={userActionPermissions?.createContent}
      >
        <DropdownMenuItem
          onSelect={() => {
            if (!userActionPermissions?.createContent) return;
            props.setOpen(false);
            props.onClickCourse();
          }}
        >
          <div className="flex items-center">
            <BookmarkIcon className="mr-2 text-muted-foreground" />
            <Text variant="compact-description">Course</Text>
          </div>
        </DropdownMenuItem>
      </NoPermissionsTooltip>
      <NoPermissionsTooltip
        hasPermission={userActionPermissions?.createContent}
      >
        <DropdownMenuItem
          onSelect={() => {
            if (!userActionPermissions?.createContent) return;
            props.setOpen(false);
            props.onClickSkill();
          }}
        >
          <div className="flex items-center">
            <CircleCheckmarkOutlineIcon className="mr-2 text-muted-foreground" />
            <Text variant="compact-description">Check-in</Text>
          </div>
        </DropdownMenuItem>
      </NoPermissionsTooltip>
      {auditsEnabled && (
        <NoPermissionsTooltip
          hasPermission={userActionPermissions?.createContent}
        >
          <DropdownMenuItem
            onSelect={() => {
              if (!userActionPermissions?.createContent) return;
              props.setOpen(false);
              props.onClickAudit();
            }}
          >
            <div className="flex items-center">
              <ContentTypeIcon
                contentType="Audit"
                className="mr-2 text-muted-foreground"
              />
              <Text variant="compact-description">Audit</Text>
            </div>
          </DropdownMenuItem>
        </NoPermissionsTooltip>
      )}
      <NoPermissionsTooltip
        hasPermission={userActionPermissions?.createContent}
      >
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            disabled={!userActionPermissions?.createContent}
          >
            <NoPermissionsTooltip
              hasPermission={userActionPermissions?.createContent}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <FileTextOutlineIcon className="mr-2 text-muted-foreground" />
                  <Text variant="compact-description">Resource</Text>
                </div>
              </div>
            </NoPermissionsTooltip>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              <DropdownMenuItem
                onSelect={() => {
                  if (!userActionPermissions?.createContent) return;
                  props.setOpen(false);
                  requestAnimationFrame(() => props.setBulkUploadOpen(true));
                }}
              >
                <ArrowUploadIcon className="text-muted-foreground" />
                <Text variant="compact-description">Upload Resource</Text>
              </DropdownMenuItem>
              {props.richTextDocumentEnabled && (
                <DropdownMenuItem
                  disabled={props.creatingBlankDoc}
                  onSelect={props.onClickOpusDoc}
                >
                  <FilePlusOutlineIcon className="text-muted-foreground" />
                  <Text variant="compact-description">Opus Doc</Text>
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </NoPermissionsTooltip>
      <NoPermissionsTooltip
        hasPermission={userActionPermissions?.createContent}
      >
        {taskLibraryItemTypeFlagEnabled && (
          <DropdownMenuItem
            onSelect={() => {
              if (!userActionPermissions?.createContent) return;
              props.setOpen(false);
              props.onClickTaskList();
            }}
          >
            <div className="flex items-center">
              <ContentTypeIcon
                contentType="Task"
                className="mr-2 text-muted-foreground"
              />
              <Text variant="compact-description">Checklist</Text>
            </div>
          </DropdownMenuItem>
        )}
      </NoPermissionsTooltip>
      {scormEnabled && (
        <NoPermissionsTooltip
          hasPermission={userActionPermissions?.createContent}
        >
          <DropdownMenuItem
            onSelect={() => {
              if (!userActionPermissions?.createContent) return;
              props.setOpen(false);
              requestAnimationFrame(() => props.setScormDialogOpen(true));
            }}
          >
            <div className="flex items-center">
              <SuitcaseOutlineIcon className="mr-2 text-muted-foreground" />
              <Text variant="compact-description">SCORM</Text>
            </div>
          </DropdownMenuItem>
        </NoPermissionsTooltip>
      )}
    </>
  );
};

export default NavMenuAddButton;
